import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { styled, Column, Row, Grid, IconButton, useSnackbar, Snackbar, AppIcon, Theme, } from '@streem/ui-react';
import { scrubDataId } from '@streem/toolbox';
import { recordElementClicked } from '@streem/analytics';
export const CallDetailKeyValueTile = ({ title, keyValuePairs, maxValuesToDisplay = 2, columnsPerRow, dataIdPrefix, keyValueStyles, keyValueTitleStyles = {}, keyValueTileStyles = {}, isTrayOpen, }) => {
    const tileBottomPadding = keyValueTileStyles.bottomPadding ?? '0.5rem';
    return (_jsx(_Fragment, { children: _jsxs(Tile, { bottomPadding: tileBottomPadding, children: [title && (_jsx(Title, { fontSize: keyValueTitleStyles.fontSize ?? keyValueStyles.fontSize, titleOnly: keyValuePairs.length === 0, "data-testid": scrubDataId(`${dataIdPrefix}-tile-title-${title}`), marginBottom: keyValueTitleStyles.marginBottom ?? '1rem', children: title })), _jsx(KeyValues, { title: title ?? undefined, columnsPerRow: columnsPerRow, children: keyValuePairs.map(({ key, value, isCopyableYn, shouldMask, displayAsPillYn, highlightedBackgroundYn, }, idx) => {
                        return (_jsx(KeyValue, { k: key, v: value, maxValuesToDisplay: maxValuesToDisplay, fontSize: keyValueStyles.fontSize, dataIdPrefix: scrubDataId(`${dataIdPrefix}-${title ?? ''}-${key}-${idx}`), analyticsId: scrubDataId(`${dataIdPrefix}-${key}`), isTrayOpen: isTrayOpen, capabilities: {
                                isCopyableYn: isCopyableYn,
                                highlightedBackgroundYn: highlightedBackgroundYn,
                                displayAsPillYn: displayAsPillYn,
                            }, shouldMask: shouldMask }, `${dataIdPrefix}-${idx}-key`));
                    }) })] }) }));
};
export const convertValueToStringArray = (value) => {
    if (typeof value === 'string') {
        return [value];
    }
    return value;
};
const KeyValue = ({ k, v, widthPercentage, maxValuesToDisplay, fontSize, dataIdPrefix, capabilities, analyticsId, isTrayOpen, shouldMask, }) => {
    const valueAsArray = convertValueToStringArray(v);
    const displayValues = valueAsArray.slice(0, maxValuesToDisplay);
    const remainingValueCount = valueAsArray.length - displayValues.length;
    let otherValues = '';
    if (remainingValueCount > 1) {
        otherValues = ` + ${remainingValueCount} others`;
    }
    else if (remainingValueCount === 1) {
        otherValues = ` + ${remainingValueCount} other`;
    }
    const keyStyles = widthPercentage ? { width: `${widthPercentage}%` } : {};
    const canCopyElementToClipboard = capabilities.isCopyableYn;
    const highlightedBackgroundYn = capabilities.highlightedBackgroundYn ?? false;
    const displayAsPill = capabilities.displayAsPillYn ?? false;
    return (_jsxs("div", { style: keyStyles, children: [_jsx(KeyRow, { fontSize: fontSize, "data-testid": `${dataIdPrefix}-key`, children: k }), displayValues.map((displayValue, idx) => {
                const isLastValue = idx === displayValues.length - 1;
                const clarityPropName = shouldMask ? 'data-clarity-mask' : 'data-clarity-unmask';
                if (canCopyElementToClipboard) {
                    return (_jsx(CopyableValue, { dataIdPrefix: dataIdPrefix, displayValue: displayValue, idx: idx, fontSize: fontSize, isLastValue: isLastValue, otherValues: otherValues, analyticsId: analyticsId, isTrayOpen: isTrayOpen, shouldMask: shouldMask }, `${dataIdPrefix}-${displayValue}-${idx}`));
                }
                else if (displayAsPill) {
                    return (_jsx(ValueRowWithPill, { fontSize: fontSize, "data-testid": scrubDataId(`${dataIdPrefix}-value-${idx}`), highlightedBackgroundYn: highlightedBackgroundYn, ...{
                            [clarityPropName]: 'true',
                        }, children: `${displayValue}${isLastValue ? otherValues : ''}` }, `${dataIdPrefix}-${displayValue}-${idx}`));
                }
                else {
                    return (_jsx(ValueRow, { fontSize: fontSize, "data-testid": scrubDataId(`${dataIdPrefix}-value-${idx}`), ...{
                            [clarityPropName]: 'true',
                        }, children: `${displayValue}${isLastValue ? otherValues : ''}` }, `${dataIdPrefix}-${displayValue}-${idx}`));
                }
            })] }));
};
const CopyableValue = ({ dataIdPrefix, displayValue, idx, fontSize, isLastValue, otherValues, isTrayOpen, analyticsId, shouldMask, }) => {
    const { addNotification, snackbarNotifications, scheduleNotificationForRemoval } = useSnackbar();
    const handleAdd = () => {
        recordElementClicked(`${analyticsId}-copy-button`);
        navigator.clipboard.writeText(displayValue);
        addNotification({
            id: displayValue,
            message: 'Copied To Clipboard',
            icon: _jsx(AppIcon, { color: Theme.colors.green50, name: "DoneIcon" }),
        });
        scheduleNotificationForRemoval(displayValue, 3000);
    };
    return (_jsxs(ValueRow, { fontSize: fontSize, children: [_jsx("span", { "data-testid": scrubDataId(`${dataIdPrefix}-value-${idx}`), ...{ [shouldMask ? 'data-clarity-mask' : 'data-clarity-unmask']: 'true' }, children: `${displayValue}${isLastValue ? otherValues : ''}` }), _jsx(CopyToClipboardButton, { handleAdd: handleAdd }), _jsx(Snackbar, { content: snackbarNotifications, position: { vertical: 'top', horizontal: 'center' }, isTrayOpen: isTrayOpen })] }, `${dataIdPrefix}-${displayValue}-${idx}`));
};
const CopyToClipboardButton = ({ handleAdd }) => {
    return (_jsx(IconButton, { iconName: "CopyIcon", label: "Copy Plan Number", background: "grey01A50", size: "medium", onClick: handleAdd }));
};
const Tile = styled(Column)(({ theme, bottomPadding }) => ({
    backgroundColor: theme.colors.grey01A50,
    borderRadius: '0.5rem',
    flexGrow: 1,
    padding: `0.5rem 1rem ${bottomPadding} 1rem`,
}));
const KeyValues = styled(Grid)(props => ({
    gridTemplateColumns: `repeat(${props.columnsPerRow}, 1fr)`,
    columnGap: '1rem',
    rowGap: '1rem',
    paddingLeft: props.title && props.columnsPerRow === 1 ? '.75rem' : '0',
}));
const Title = styled.div(props => ({
    marginBottom: props.titleOnly ? undefined : props.marginBottom,
    fontSize: props.fontSize,
    lineHeight: props.theme.text.lineHeight.medium,
    fontFamily: props.theme.text.headingFontFamily,
    fontWeight: 500,
    textTransform: 'capitalize',
    overflowWrap: 'anywhere',
}));
const KeyRow = styled.div(props => ({
    fontSize: props.fontSize,
    lineHeight: props.theme.text.lineHeight.medium,
    fontFamily: props.theme.text.headingFontFamily,
    fontWeight: 500,
    marginBottom: '0.5rem',
}));
const ValueRow = styled(Row)(props => ({
    fontFamily: props.theme.text.bodyFontFamily,
    fontSize: props.fontSize,
    lineHeight: props.theme.text.lineHeight.medium,
    alignItems: 'center',
    fontWeight: 400,
    overflowWrap: 'anywhere',
}));
const ValueRowWithPill = styled(Row)(props => ({
    fontFamily: props.theme.text.bodyFontFamily,
    fontSize: props.fontSize,
    backgroundColor: props.highlightedBackgroundYn
        ? props.theme.colors.green10
        : props.theme.colors.grey10,
    padding: '.125rem .5rem',
    borderRadius: '.375rem',
    lineHeight: props.theme.text.lineHeight.medium,
    alignItems: 'center',
    fontWeight: 500,
    width: 'fit-content',
}));
//# sourceMappingURL=call_detail_key_value_tile.js.map